import { useCreate, useNotify, type InputProps } from "ra-core";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { useIsMobile } from "@/hooks/use-mobile";

const MAX_CATEGORIES = 1000;

/**
 * Almikia Epic EC2a: the Proveedor category dropdown, fed by `supplier_categories` (fetched
 * once, filtered locally) with an inline "Crear categoria" option, the same inline-create
 * pattern as the Empresa picker. The server stamps `created_by`; RLS and the guard stay the
 * authority on who may create and on duplicate names.
 */
export const SupplierCategoryInput = ({
  validate,
}: Pick<InputProps, "validate">) => {
  const [create] = useCreate();
  const notify = useNotify();
  const isMobile = useIsMobile();

  const handleCreate = async (name?: string) => {
    if (!name) return;
    try {
      return await create(
        "supplier_categories",
        { data: { name: name.trim(), active: true } },
        { returnPromise: true },
      );
    } catch (error) {
      notify("resources.contacts.supplier_category.create_error", {
        type: "error",
        messageArgs: {
          _: error instanceof Error ? error.message : undefined,
        },
      });
    }
  };

  return (
    <ReferenceInput
      source="supplier_category_id"
      reference="supplier_categories"
      perPage={MAX_CATEGORIES}
      filter={{ active: true }}
      sort={{ field: "name", order: "ASC" }}
    >
      <AutocompleteInput
        label="resources.contacts.fields.supplier_category_id"
        optionText="name"
        helperText={false}
        onCreate={handleCreate}
        createItemLabel="resources.contacts.supplier_category.create_item"
        createLabel="resources.contacts.supplier_category.create_label"
        validate={validate}
        modal={isMobile}
      />
    </ReferenceInput>
  );
};
